import type {FaceLandmarker} from "@mediapipe/tasks-vision";
import type {FacePoint} from "./makeupPreview";
let detector:Promise<FaceLandmarker>|undefined;
async function getDetector(){
  if(!detector) detector=(async()=>{
    const {FaceLandmarker,FilesetResolver}=await import("@mediapipe/tasks-vision");
    return FaceLandmarker.createFromOptions(await FilesetResolver.forVisionTasks("/models/face/wasm"),{baseOptions:{modelAssetPath:"/models/face/face_landmarker.task",delegate:"CPU"},runningMode:"IMAGE",numFaces:2});
  })().catch(e=>{detector=undefined;throw e;});
  return detector;
}
export async function locateFace(image:HTMLImageElement):Promise<FacePoint[]> {
  let timer:ReturnType<typeof setTimeout>|undefined;
  let model:FaceLandmarker;
  try{model=await Promise.race([getDetector(),new Promise<FaceLandmarker>((_,reject)=>{timer=setTimeout(()=>reject(Error("The photo model took too long to load. Check your connection and try again.")),45000);})]);}finally{clearTimeout(timer);}
  // A single inference per still photo; shade changes never rerun detection.
  await new Promise<void>(resolve=>requestAnimationFrame(()=>resolve()));
  const result=model.detect(image);
  if(result.faceLandmarks.length!==1)throw Error(result.faceLandmarks.length>1?"Choose a photo with just you in it, so the color goes on the right face.":"We couldn't find a clear face. Try a front-facing photo in even light.");
  const points=result.faceLandmarks[0];
  const faceWidth=Math.abs(points[234].x-points[454].x);
  if(faceWidth<.12)throw Error("Your face is a little too far away. Try a closer portrait.");
  return points.map(({x,y,z})=>({x,y,z}));
}
export function loadPhoto(src:string):Promise<HTMLImageElement> {
  return new Promise((resolve,reject)=>{const img=new Image();if(/^https?:/.test(src))img.crossOrigin="anonymous";img.onload=()=>resolve(img);img.onerror=()=>reject(Error("This photo couldn't open. Try a JPG, PNG or WebP image."));img.src=src;});
}
export async function readPreviewPhoto(file:File):Promise<string> {
  if(!["image/jpeg","image/png","image/webp"].includes(file.type))throw Error("Choose a JPG, PNG or WebP photo. For HEIC, export it as JPEG first.");
  if(file.size>15*1024*1024)throw Error("Please choose a photo smaller than 15 MB.");
  const url=URL.createObjectURL(file);
  try {const img=await loadPhoto(url);const scale=Math.min(1,1200/Math.max(img.naturalWidth,img.naturalHeight));const canvas=document.createElement("canvas");canvas.width=Math.round(img.naturalWidth*scale);canvas.height=Math.round(img.naturalHeight*scale);canvas.getContext("2d")!.drawImage(img,0,0,canvas.width,canvas.height);return canvas.toDataURL("image/jpeg",.93);}
  finally{URL.revokeObjectURL(url);}
}
